import { z } from 'zod'

export const INDUSTRY_OPTIONS = [
  'Technology',
  'Marketing & Advertising',
  'Finance',
  'Healthcare',
  'Education',
  'E-commerce',
  'Media & Entertainment',
  'Consulting',
  'Legal',
  'Other',
] as const

export type Industry = (typeof INDUSTRY_OPTIONS)[number]

export const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'] as const

export type CompanySize = (typeof COMPANY_SIZE_OPTIONS)[number]

export const companyOnboardingSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Company name must be at least 2 characters')
      .max(100, 'Company name must be 100 characters or fewer'),
    industry: z.enum(INDUSTRY_OPTIONS, { message: 'Select an industry' }),
    industryOther: z.string().trim().max(100).optional(),
    size: z.enum(COMPANY_SIZE_OPTIONS, { message: 'Select a company size' }),
    website: z
      .string()
      .trim()
      .url('Invalid URL')
      .refine((url) => /^https?:\/\//i.test(url), { message: 'URL must use http or https' })
      .optional()
      .or(z.literal('')),
    description: z
      .string()
      .trim()
      .max(500, 'Description must be 500 characters or fewer')
      .optional()
      .or(z.literal('')),
    adminName: z.string().trim().min(1, 'Name is required').max(100),
    adminRole: z.string().trim().min(1, 'Role is required').max(100),
  })
  .refine(
    (data) => data.industry !== 'Other' || (data.industryOther && data.industryOther.length > 0),
    { message: 'Please specify your industry', path: ['industryOther'] },
  )

export type CompanyOnboardingInput = z.infer<typeof companyOnboardingSchema>

export const updateCompanySchema = z
  .object({
    name: z.string().trim().min(2).max(100).optional(),
    logoUrl: z.string().url().refine((url) => /^https?:\/\//i.test(url), { message: 'URL must use http or https' }).optional().or(z.literal('')).or(z.literal(null)),
    industry: z.enum(INDUSTRY_OPTIONS).optional(),
    industryOther: z.string().trim().max(100).optional(),
    size: z.enum(COMPANY_SIZE_OPTIONS).optional(),
    website: z.string().trim().url().refine((url) => /^https?:\/\//i.test(url), { message: 'URL must use http or https' }).optional().or(z.literal('')),
    description: z.string().trim().max(500).optional().or(z.literal('')),
    adminName: z.string().trim().min(1).max(100).optional(),
    adminRole: z.string().trim().min(1).max(100).optional(),
  })
  .refine(
    (data) => !data.industry || data.industry !== 'Other' || (data.industryOther && data.industryOther.length > 0),
    { message: 'Please specify your industry', path: ['industryOther'] },
  )

export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>

export const reviewCompanySchema = z
  .object({
    companyId: z.string().uuid('Invalid company ID'),
    action: z.enum(['approve', 'reject', 'suspend', 'unsuspend']),
    reason: z.string().trim().max(1000).optional(),
  })
  .refine(
    (data) => data.action !== 'reject' || (data.reason && data.reason.length > 0),
    { message: 'Rejection reason is required', path: ['reason'] },
  )

export type ReviewCompanyInput = z.infer<typeof reviewCompanySchema>
